import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../shared/Icon';
import type { IconName } from '../shared/icons';
import { ApiError } from '../api';
import { useEnterContext, useMe } from '../hooks';
import type { RoleType, TrackerContext } from '../types';

/**
 * Вход в трекер. Пользователь не заводит трекер сам и не придумывает ему название — он выбирает
 * роль, а трекер этой роли открывается или создаётся при первом входе.
 *
 * <p>Если контекст всего один, выбирать нечего: входим сразу, чтобы не показывать экран из одной
 * кнопки.
 */
export function EntryPage() {
  const me = useMe();
  const enter = useEnterContext();
  const navigate = useNavigate();
  const [chosen, setChosen] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'МедКабинет Трекер';
  }, []);

  const open = (context: TrackerContext) => {
    if (enter.isPending) return;
    setChosen(contextKey(context));
    enter.mutate(context, {
      onSuccess: (tenant) => navigate(`/t/${tenant.id}/queues`),
      onError: () => setChosen(null),
    });
  };

  const contexts = me.data?.contexts ?? [];
  const available = contexts.filter((c) => c.available);

  // Единственный доступный контекст открываем без вопросов.
  useEffect(() => {
    if (me.isSuccess && available.length === 1 && !enter.isPending && !chosen) {
      open(available[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.isSuccess, available.length]);

  if (me.isLoading) return <div className="state">Загрузка…</div>;
  if (me.isError) {
    // Неавторизованного клиент API уже уводит на вход; здесь — настоящая ошибка сервиса.
    const status = (me.error as ApiError).status;
    return (
      <div className="state">
        {status === 401 ? 'Переходим ко входу…' : 'Сервис трекера недоступен'}
      </div>
    );
  }

  return (
    <div className="entry-page">
      <div className="entry-wrap">
        <div className="entry-head">
          <div className="entry-logo">
            <div className="entry-logo-icon">
              <Icon name="activity" size={19} />
            </div>
            <div className="entry-logo-name">МедКабинет Трекер</div>
          </div>
          <h1>Выберите роль</h1>
          <p>Роль определяет, в каком трекере вы работаете — личном или своей организации</p>
        </div>

        {contexts.length === 0 ? (
          <div className="card state">
            <div className="state-title">Нет доступных ролей</div>
            Ваша учётная запись не даёт доступа к трекеру. Обратитесь к администратору организации.
          </div>
        ) : (
          <div className="role-list">
            {contexts.map((context) => {
              const key = contextKey(context);
              const view = PRESENTATION[context.roleType];
              const busy = chosen === key && enter.isPending;

              return (
                <button
                  type="button"
                  key={key}
                  data-tone={view.tone}
                  className={`role-card ${context.available ? '' : 'unavailable'}`}
                  disabled={!context.available || enter.isPending}
                  onClick={() => open(context)}
                >
                  <div className="role-icon">
                    <Icon name={view.icon} size={20} />
                  </div>
                  <div className="role-body">
                    <div className="role-title">{context.tenantName ?? view.title}</div>
                    <div className="role-desc">
                      {context.available
                        ? context.creates
                          ? `${view.title} · трекер будет создан при первом входе`
                          : view.title
                        : context.unavailableReason}
                    </div>
                  </div>
                  {busy && <div className="role-status">Входим…</div>}
                </button>
              );
            })}
          </div>
        )}

        {enter.isError && (
          <div className="form-error" style={{ textAlign: 'center' }}>
            {(enter.error as ApiError).message}
          </div>
        )}
      </div>
    </div>
  );
}

/** Контекст различается ролью и компанией: у одного человека их может быть несколько. */
const contextKey = (context: TrackerContext): string =>
  `${context.roleType}:${context.companyId ?? '-'}`;

interface Presentation {
  title: string;
  icon: IconName;
  tone: 'individual' | 'employee' | 'admin';
}

const PRESENTATION: Record<RoleType, Presentation> = {
  PERSON: { title: 'Личный трекер', icon: 'user', tone: 'individual' },
  EMPLOYEE: { title: 'Сотрудник организации', icon: 'users', tone: 'employee' },
  ADMINISTRATOR: { title: 'Администратор организации', icon: 'building', tone: 'admin' },
  DEPARTMENT_ADMINISTRATOR: { title: 'Администратор подразделения', icon: 'sitemap', tone: 'admin' },
  SYSTEM_ADMINISTRATOR: { title: 'Системный администратор', icon: 'settings', tone: 'admin' },
};
